import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents } from '@/features/catalog/catalog';
import { fetchDashboard, type DashboardSummary } from './dashboard';
import { RepartoDelMes } from './RepartoPage';
import { fetchOrganizationDebt, type DeudaDeLaOrganizacion } from '@/features/closing/closing';
import { listAllParticipants, type Participant } from '@/features/works/works';
import { SelectorPeriodo } from '@/features/period/SelectorPeriodo';

interface Props {
  /**
   * Ir al listado de trabajos del mes que se está mirando.
   *
   * Antes cada tarjeta llevaba su propio filtro —«pendiente de facturar» abría
   * la lista ya recortada—. Trabajos dejó de tener filtros: el mes es su único
   * eje, y llegar a una lista recortada sin poder ver ni sacar el recorte era
   * el peor de los dos mundos. La tarjeta informa; la lista es la lista.
   */
  onVerTrabajos: () => void;
  onIrAConfig: () => void;
  /** El período no viaja como argumento: lo lee cada pantalla del contexto. */
  onVerLiquidacion: () => void;
  onVerReportes: () => void;
  /** Abre un trabajo del detalle de abajo. */
  onVerTrabajo: (workId: string) => void;
  /** Vuelve a Inicio: ahora el resumen es una pantalla y no la pestaña. */
  onVolver: () => void;
}

/**
 * Inicio / Dashboard (Spec §32).
 *
 * Dos reglas gobiernan esta pantalla:
 *
 * 1. **Todo KPI es interactivo** (Spec §33, §35): tocarlo abre el listado
 *    filtrado que lo genera. Un número que no se puede abrir obliga a rehacer
 *    el filtro a mano y, peor, no se puede verificar.
 * 2. **Sin precio de novillo se muestran los kg y se dice "pendiente"**, nunca
 *    un cero. Los kilogramos son un dato operativo real; el equivalente en
 *    pesos, sin precio, no existe todavía.
 */
export function DashboardPage({
  onVerTrabajos,
  onIrAConfig,
  onVerLiquidacion,
  onVerReportes,
  onVerTrabajo,
  onVolver,
}: Props) {
  const { activeOrg } = useOrg();
  const orgId = activeOrg?.organization_id;

  // El período NO se guarda acá: vive en `PeriodProvider` para que volver de
  // Reportes o de Liquidación no lo reinicie.
  const { year, month } = usePeriod();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Lo que la organización le debe a cada uno, y quién es quién.
   *
   * Los dos existen por una sola razón: **que esta pantalla y Liquidación digan
   * el mismo número**. El saldo de la liquidación reconoce la plata que entró
   * en efectivo a la veterinaria y todavía no llegó a quien hizo el trabajo; si
   * acá se mostrara el saldo sin eso, las dos pantallas dirían cosas distintas
   * del mismo mes y ninguna de las dos sería creíble.
   */
  const [deuda, setDeuda] = useState<DeudaDeLaOrganizacion[]>([]);
  const [gente, setGente] = useState<Participant[]>([]);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      setCargando(true);
      try {
        const d = await fetchDashboard(orgId, year, month);
        if (cancelado) return;
        setData(d);
        setError(null);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month]);

  // Aparte del resumen y en silencio: si algo de esto falla, la pantalla sigue
  // mostrando la producción del mes, que es lo que se viene a ver.
  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      try {
        const [dd, pp] = await Promise.all([
          fetchOrganizationDebt(orgId, year, month),
          listAllParticipants(orgId),
        ]);
        if (cancelado) return;
        setDeuda(dd);
        setGente(pp);
      } catch {
        // Sin esto el resultado por persona no se dibuja, y está bien: es
        // preferible no mostrarlo a mostrar un saldo que no coincide con el de
        // Liquidación.
        if (!cancelado) setDeuda([]);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month]);

  /** Lo que la organización le debe a este participante en el período. */
  const deudaDe = (pid: string) => deuda.find((d) => d.participant_id === pid)?.amount_cents ?? 0;

  if (!activeOrg || !orgId) return null;

  const p = data?.production;
  // Una organización de una sola persona no tiene saldos entre participantes:
  // mostrar "mi resultado" ahí sería repetir la producción con otro nombre.
  const muestraSaldos = data?.org_kind === 'team';

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="dashboard">
      <button
        type="button"
        onClick={onVolver}
        className="min-h-11 self-start text-sm underline opacity-70"
        data-testid="dashboard-volver"
      >
        ‹ Volver
      </button>

      {/* Selector de mes compartido con Liquidación: era el mismo problema en
          dos pantallas y tenía dos soluciones distintas —una parcial y una
          inexistente—. */}
      <SelectorPeriodo testId="dashboard-periodo" />

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="dashboard-error">
          {error}
        </p>
      )}
      {cargando && !data && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

      {p && (
        <>
          {/* --- Producción del mes (tarea 7.1) --- */}
          <section className="grid grid-cols-2 gap-3" data-testid="kpi-production">
            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-kg"
            >
              <span className="text-lg font-semibold">{p.total_kg} kg</span>
              <span className="text-xs text-[var(--tinta-tenue)]">de novillo en el mes</span>
            </button>

            <button
              type="button"
              onClick={() =>
                p.total_cents === null ? data.is_admin && onIrAConfig() : onVerTrabajos()
              }
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-equivalent"
            >
              {p.total_cents === null ? (
                <>
                  <span className="text-sm font-semibold text-[var(--aviso)]">
                    Precio de novillo pendiente
                  </span>
                  {/* Cargar precios es tarea del administrador. A un miembro no
                      se le ofrece un atajo que la base le va a negar. */}
                  <span className="text-xs text-[var(--tinta-suave)]">
                    {data.is_admin ? (
                      <span className="underline">Cargar precio</span>
                    ) : (
                      'Lo carga un administrador'
                    )}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-lg font-semibold">{formatCents(p.total_cents)}</span>
                  <span className="text-xs text-[var(--tinta-tenue)]">total generado</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-works"
            >
              <span className="text-lg font-semibold">{p.works_count}</span>
              <span className="text-xs text-[var(--tinta-tenue)]">
                trabajo{p.works_count === 1 ? '' : 's'}
              </span>
            </button>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-20 flex-col justify-center superficie p-3 text-left"
              data-testid="kpi-mobility"
            >
              <span className="text-lg font-semibold">
                {p.mobility_cents === null ? 'Pendiente' : formatCents(p.mobility_cents)}
              </span>
              <span className="text-xs text-[var(--tinta-tenue)]">de movilidad</span>
            </button>
          </section>

          {p.pending_count > 0 && (
            <p
              className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
              data-testid="dashboard-pending-note"
            >
              {p.pending_count === 1
                ? 'Hay 1 trabajo sin precio de referencia.'
                : `Hay ${p.pending_count} trabajos sin precio de referencia.`}{' '}
              Los kilogramos están cargados; el equivalente en pesos se calcula cuando se cargue el
              precio.
            </p>
          )}

          {/* --- Pendientes (tarea 7.2) --- */}
          <section className="flex flex-col superficie" data-testid="kpi-pending">
            <h2 className="border-b border-[var(--linea)] px-4 py-2 text-xs font-medium uppercase opacity-60">
              Pendientes
            </h2>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-16 items-center justify-between gap-3 border-b border-[var(--linea)] px-4 py-3 text-left"
              data-testid="card-pending-bill"
            >
              <span className="min-w-0">
                <span className="block text-sm">🧾 Pendiente de facturar</span>
                <span className="block text-xs text-[var(--tinta-suave)]">
                  {p.pending_to_bill.count} trabajo{p.pending_to_bill.count === 1 ? '' : 's'}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold">
                {p.pending_to_bill.amount_cents === null
                  ? 'Pendiente'
                  : formatCents(p.pending_to_bill.amount_cents)}
                <span className="ml-2 opacity-40">›</span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => onVerTrabajos()}
              className="flex min-h-16 items-center justify-between gap-3 px-4 py-3 text-left"
              data-testid="card-pending-collect"
            >
              <span className="min-w-0">
                <span className="block text-sm">💰 Pendiente de cobrar</span>
                <span className="block text-xs text-[var(--tinta-suave)]">
                  {p.pending_to_collect.count} trabajo{p.pending_to_collect.count === 1 ? '' : 's'}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold">
                {p.pending_to_collect.amount_cents === null
                  ? 'Pendiente'
                  : formatCents(p.pending_to_collect.amount_cents)}
                <span className="ml-2 opacity-40">›</span>
              </span>
            </button>
          </section>

          {/* --- Mi resultado (tarea 7.3). Sólo con equipo. --- */}
          {muestraSaldos && (
            <section className="flex flex-col gap-2 superficie p-4" data-testid="my-result">
              <h2 className="text-xs font-medium uppercase opacity-60">Mi resultado</h2>

              {data.my_result === null ? (
                <p
                  className="text-sm text-[var(--tinta-suave)]"
                  data-testid="my-result-unavailable"
                >
                  {p.pending_count > 0
                    ? 'El saldo se calcula cuando todos los trabajos del mes tengan precio. Con un trabajo pendiente, cualquier saldo sería un número incompleto.'
                    : 'No participás económicamente en este período.'}
                </p>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="opacity-70">Ganaste en el mes</span>
                    <span data-testid="my-earned">
                      {formatCents(
                        data.my_result.earned_honoraria_cents +
                          data.my_result.earned_mobility_cents,
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span
                      className="opacity-70"
                      title="Total facturado a mi nombre en el período, cobrado o no."
                    >
                      Cobrás o vas a cobrar
                    </span>
                    <span data-testid="my-billed">{formatCents(data.my_result.billed_cents)}</span>
                  </div>
                  {data.my_result.adjustments_cents !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="opacity-70">Ajustes</span>
                      <span>{formatCents(data.my_result.adjustments_cents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-[var(--linea)] pt-2 text-sm font-semibold">
                    <span>Para emparejar</span>
                    <span
                      className={
                        data.my_result.balance_cents < 0
                          ? 'text-[var(--peligro)]'
                          : 'text-[var(--exito)]'
                      }
                      data-testid="my-balance"
                    >
                      {formatCents(data.my_result.balance_cents)}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--tinta-tenue)]">
                    {data.my_result.balance_cents === 0
                      ? 'Estás a cero este mes.'
                      : data.my_result.balance_cents < 0
                        ? 'Cobraste de más: te toca transferir.'
                        : 'Cobraste de menos: te tienen que transferir.'}
                  </p>
                </>
              )}
            </section>
          )}

          {/* Entrada a la liquidación del mes. La ven todos: un miembro accede
              a SU propia línea y a las transferencias donde interviene. */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onVerLiquidacion}
              className="min-h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
              data-testid="ver-liquidacion"
            >
              Liquidación
            </button>
            <button
              type="button"
              onClick={onVerReportes}
              className="min-h-12 flex-1 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3 text-sm"
              data-testid="ver-reportes"
            >
              Reportes
            </button>
          </div>

          {/* --- Quién facturó cuánto y quién ganó cuánto. Sólo admin. ---

              Antes acá había un renglón por persona con el SALDO y nada más.
              El saldo es la resta final —lo que falta mover para emparejar— y
              por sí solo no contesta la pregunta con la que se entra a esta
              pantalla en la mitad del mes: cuánto facturó cada uno y cuánto
              ganó. Los dos números ya venían en la misma respuesta del
              servidor y se estaban tirando.

              Se dice igual que en Liquidación —mismos títulos, mismas frases,
              mismo orden— a propósito: son los mismos importes, y verlos
              escritos distinto en dos pantallas hace dudar de si son lo mismo.
              La única diferencia real es cuándo: acá se ve durante el mes, y
              allá queda congelado al cerrar. */}
          {data.is_admin && muestraSaldos && data.org_result && (
            <section className="flex flex-col superficie" data-testid="org-result">
              <h2 className="border-b border-[var(--linea)] px-4 py-2 text-xs font-medium uppercase opacity-60">
                Quién facturó y quién ganó
              </h2>

              <ul className="divide-y divide-[var(--linea)] px-4">
                {data.org_result.map((r) => {
                  const gano = r.earned_honoraria_cents + r.earned_mobility_cents;
                  const saldo = r.balance_cents + deudaDe(r.participant_id);
                  return (
                    <li
                      key={r.participant_id}
                      className="flex flex-col gap-1 py-3"
                      data-testid="org-result-row"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 font-semibold">
                          {r.display_name}
                          {/* Por el TIPO, no por el nombre: la cuenta técnica se
                              puede llamar «Caja» o como quieran. Acá va a parar
                              lo que no tiene facturador asignado, y sin esta
                              aclaración se lee como un socio más. */}
                          {gente.find((g) => g.id === r.participant_id)?.kind === 'org_account' && (
                            <span
                              className="ml-2 rounded-full bg-[var(--superficie-suave)] px-2 py-0.5 text-xs font-normal opacity-80"
                              title="No es una persona. Acá va lo que no tiene facturador asignado, para que las cuentas cierren en cero."
                              data-testid="org-result-cuenta-tecnica"
                            >
                              cuenta de la organización
                            </span>
                          )}
                        </span>
                        <span
                          className="shrink-0 text-lg font-semibold tabular-nums"
                          data-testid="org-result-gano"
                        >
                          {formatCents(gano)}
                        </span>
                      </div>

                      <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                        <span title="Total facturado a nombre de esta persona en el mes, cobrado o no.">
                          Facturó
                        </span>
                        <span className="tabular-nums" data-testid="org-result-facturo">
                          {formatCents(r.billed_cents)}
                        </span>
                      </div>

                      {r.earned_mobility_cents !== 0 && (
                        <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                          <span>De lo que ganó, movilidad</span>
                          <span className="tabular-nums">
                            {formatCents(r.earned_mobility_cents)}
                          </span>
                        </div>
                      )}

                      {r.adjustments_cents !== 0 && (
                        <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                          <span>Ajustes</span>
                          <span className="tabular-nums">{formatCents(r.adjustments_cents)}</span>
                        </div>
                      )}

                      {deudaDe(r.participant_id) > 0 && (
                        <div className="flex justify-between text-sm text-[var(--tinta-suave)]">
                          <span>La organización le debe</span>
                          <span className="tabular-nums">
                            {formatCents(deudaDe(r.participant_id))}
                          </span>
                        </div>
                      )}

                      {/* El saldo en castellano. «-$1.120.125» obliga a recordar
                          de qué lado está el menos: si es plata que falta cobrar
                          o plata que hay que poner. */}
                      <p
                        className={`mt-1 text-sm font-medium ${
                          saldo === 0
                            ? 'opacity-60'
                            : saldo > 0
                              ? 'text-[var(--exito)]'
                              : 'text-[var(--peligro)]'
                        }`}
                        data-testid="org-result-saldo"
                      >
                        {saldo === 0
                          ? 'Está a mano'
                          : saldo > 0
                            ? `Le tienen que transferir ${formatCents(saldo)}`
                            : `Tiene que transferir ${formatCents(-saldo)}`}
                      </p>
                    </li>
                  );
                })}
              </ul>

              {/* El total, para contrastar contra la planilla de un vistazo. */}
              <div className="flex items-baseline justify-between gap-3 border-t border-[var(--linea)] px-4 py-3 text-sm">
                <span className="opacity-70">Total del mes</span>
                <span className="tabular-nums" data-testid="org-result-total">
                  Facturó{' '}
                  <strong>
                    {formatCents(data.org_result.reduce((a, r) => a + r.billed_cents, 0))}
                  </strong>{' '}
                  · ganó{' '}
                  <strong>
                    {formatCents(
                      data.org_result.reduce(
                        (a, r) => a + r.earned_honoraria_cents + r.earned_mobility_cents,
                        0,
                      ),
                    )}
                  </strong>
                </span>
              </div>

              <p className="px-4 pb-3 text-xs text-[var(--tinta-tenue)]">
                Los saldos del período suman cero: lo que uno cobró de más, otro lo cobró de menos.
                Al liquidar el mes, estos mismos números quedan congelados.
              </p>
            </section>
          )}

          {/* El detalle, abajo de todo y sin enlace de por medio. Estaba detrás
              de un subrayado al pie y ahí no lo encontraba nadie, siendo que es
              lo que se usa para revisar contra la planilla —o sea, casi todos
              los días mientras las dos convivan—.
              El orden es el de la pregunta: arriba cuánto ganó cada uno, abajo
              de qué trabajo salió, que es donde se mira cuando no cierra. */}
          {/* La MISMA condición que el resumen de arriba, y no una parecida.
              Con `org_result` en null —que es lo que devuelve el servidor
              mientras haya un trabajo sin precio— quedaba el detalle solo, sin
              el resumen por persona: se veía una lista de trabajos y ninguna
              respuesta a la pregunta con la que se entra acá. Aparecen los dos
              o no aparece ninguno. */}
          {data.is_admin && muestraSaldos && data.org_result && (
            <RepartoDelMes onVerTrabajo={onVerTrabajo} />
          )}
        </>
      )}
    </div>
  );
}
